<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #content>
			<!-- As "Payment unsuccessful" (Figma 7415:17771): the tile 104 from the top, the text 24 under it. -->
			<LayoutFlex padding="var(--size-24) var(--size-20)" gap="0" align-items="center">
				<LayoutFlex padding="var(--size-80) 0 0" gap="var(--size-24)" align-items="center">
					<div class="cancel-error__icon" aria-hidden="true">
						<RoseCross />
					</div>
					<LayoutFlex padding="0" gap="var(--size-12)" align-items="center">
						<Text font="--heading-h3" color="--text-dark-primary" text-align="center">Something went wrong</Text>
						<div role="alert">
							<Text font="--text-16-22-reg" color="--text-dark-primary" text-align="center">
								Your cancellation isn’t complete and your KOVA plan is still active. Please try again.
							</Text>
						</div>
					</LayoutFlex>
				</LayoutFlex>
			</LayoutFlex>
		</template>

		<template #footer>
			<LayoutFlex padding="0 var(--size-20) var(--size-24)" gap="var(--size-16)" align-items="center">
				<Button full-width @ds:click="retry">
					<Text font="--text-16-18-semi">Try again</Text>
				</Button>
				<Text font="--text-16-22-reg" color="--text-dark-primary" text-align="center">
					Any questions?
					<a class="cancel-error__support-link" :href="`mailto:${SUPPORT_EMAIL}`" @click="onContactSupport">
						Contact support
					</a>
				</Text>
			</LayoutFlex>
		</template>
	</LayoutScreen>
</template>

<script setup lang="ts">
// A cancel call that failed with no payment involved (500 / dropped connection on "Keep my plan",
// "Cancel after N payments", renewals-only under review, the commitment confirm) — DEV-35727. Same
// layout as "Payment unsuccessful" (Figma 6455:21896, now 7415:17771); the design has no variant for this case, so the
// copy is the owner-approved one (2026-10-13). Hosted in a fullscreen DS <Modal> by the screen that
// made the call, so "Try again" re-runs that same call in place. Nothing was cancelled.
//
// Analytics (agreed with analytics 2026-10-21): CANCELLATION_FAILED as a step — the modal is no route, so Show
// on mount, Done on "Try again" (`try_again`) or, with no answer, when it goes away otherwise; "Contact
// support" is `web_cancellation_failed`. No SubscriptionStateChanged: nothing was cancelled. `data.step` is
// the title of the host's own step — where the call failed (the modal looks the same over all of them).
import { onBeforeUnmount } from 'vue'
import { Button, LayoutFlex, LayoutScreen, Text } from '@simple-life-app/growth-ui-kova'
import RoseCross from '@/components/cancel/RoseCross.vue'
import { STEP_TITLE, overlayStep, trackSupportEntryPoint, type StepTitle } from '@/analytics'
import { SUPPORT_EMAIL } from '@/config'

const props = defineProps<{ step: StepTitle }>()
const emit = defineEmits<{ retry: [] }>()

const done = overlayStep(STEP_TITLE.CANCELLATION_FAILED, { step: props.step })
onBeforeUnmount(() => done())

function retry() {
	done(['try_again'])
	emit('retry')
}

function onContactSupport() {
	trackSupportEntryPoint('web_cancellation_failed')
}
</script>

<style scoped>
/* As the failed-payment tile: 64×64, radius 24, `status/error/bg`. */
.cancel-error__icon {
	width: var(--size-64);
	height: var(--size-64);
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(203, 49, 82, 0.1);
	border-radius: var(--size-24);
}

/* DS Text ships `.text a { color: inherit }` at (0,2,1): the `.text a` prefix lets the brand green win. */
.text a.cancel-error__support-link {
	color: var(--background-brand-high);
	text-decoration: underline;
}
</style>
